/**
 * 记忆召回策略（T13，纯函数，重点交付）。
 *
 * 与 UI / 存储解耦：只依赖 `MemoryEntry` 数据，便于单测与后续在对话流程里
 * 注入 system 提示前复用。
 *
 * 打分：关键词命中数 × 重要度；
 * 排序：分数降序 → 同分更新时间新的优先 → id 兜底（结果稳定）；
 * 预算：按「记忆内容字符数」累计，预算内选入，放不下下一条即停止，绝不超预算。
 */

import type { ChatMessage, MemoryEntry } from '../../core/types';

/** 默认记忆预算（字符数）。 */
export const DEFAULT_MEMORY_BUDGET_CHARS = 1200;
/** 组装 contextText 时默认取最近 6 条用户/助手消息（约 3 轮）。 */
export const DEFAULT_CONTEXT_MESSAGE_LIMIT = 6;

/** 从内容里切出长度 >= 2 的候选词（中英文混排下的轻量分词）。 */
function tokenizeContent(content: string): string[] {
  return content
    .split(/[\s,，。.!！?？、;；:：()（）[\]【】{}<>"'“”‘’_—+-]+/)
    .map((token) => token.trim())
    .filter((token) => token.length >= 2);
}

/**
 * 一条记忆的关键词集合 = 标签 ∪ 内容中的候选词。
 * 标签是人工标注的召回键，优先；内容词用于补充召回面。
 */
export function extractKeywords(memory: MemoryEntry): string[] {
  const keywords = new Set<string>();
  for (const tag of memory.tags) {
    const trimmed = tag.trim().toLowerCase();
    if (trimmed !== '') keywords.add(trimmed);
  }
  for (const token of tokenizeContent(memory.content)) {
    keywords.add(token.toLowerCase());
  }
  return [...keywords];
}

/** 关键词在 contextText 中的命中个数（不区分大小写；子串匹配以兼容中文）。 */
export function countKeywordHits(memory: MemoryEntry, contextText: string): number {
  if (contextText.trim() === '') return 0;
  const haystack = contextText.toLowerCase();
  let hits = 0;
  for (const keyword of extractKeywords(memory)) {
    if (haystack.includes(keyword)) hits += 1;
  }
  return hits;
}

/** 单条记忆得分 = 关键词命中数 × 重要度。 */
export function scoreMemory(memory: MemoryEntry, contextText: string): number {
  return countKeywordHits(memory, contextText) * memory.importance;
}

/**
 * 召回：按分数降序（同分更新时间新的优先）在字符预算内选入。
 * - 命中 0 次的记忆不选；
 * - 遇到放不下的下一条即停止，保证 `Σ content.length <= budgetChars`；
 * - contextText 为空或预算 <= 0 返回空数组。
 */
export function selectMemories(
  memories: readonly MemoryEntry[],
  contextText: string,
  budgetChars: number,
): MemoryEntry[] {
  const budget = Number.isFinite(budgetChars) ? Math.floor(budgetChars) : 0;
  if (budget <= 0 || contextText.trim() === '') return [];

  const ranked = memories
    .map((memory) => ({ memory, score: scoreMemory(memory, contextText) }))
    .filter((entry) => entry.score > 0)
    .sort(
      (a, b) =>
        b.score - a.score ||
        b.memory.updatedAt - a.memory.updatedAt ||
        (a.memory.id < b.memory.id ? -1 : a.memory.id > b.memory.id ? 1 : 0),
    );

  const selected: MemoryEntry[] = [];
  let used = 0;
  for (const { memory } of ranked) {
    const cost = memory.content.length;
    if (used + cost > budget) break;
    selected.push(memory);
    used += cost;
  }
  return selected;
}

/**
 * 渲染成可注入 system 提示的文本块：带 `[记忆]` 标题与条目编号。
 * 没有选中记忆时返回空串。
 */
export function buildMemoryBlock(selected: readonly MemoryEntry[]): string {
  if (selected.length === 0) return '';
  const lines = selected.map((memory, index) => `${index + 1}. ${memory.content}`);
  return ['[记忆]', ...lines].join('\n');
}

/**
 * 与对话接线（只提供工具函数，不改动 T06 页面）：
 * 把最近若干条「用户 / 助手」消息文本拼成 contextText，供对话页调用。
 */
export function collectMemoryContext(
  messages: readonly Pick<ChatMessage, 'role' | 'content'>[],
  limit: number = DEFAULT_CONTEXT_MESSAGE_LIMIT,
): string {
  const max = Number.isFinite(limit) ? Math.floor(limit) : 0;
  if (max <= 0) return '';
  const relevant = messages.filter(
    (message) => message.role === 'user' || message.role === 'assistant',
  );
  return relevant
    .slice(-max)
    .map((message) => message.content.trim())
    .filter((content) => content !== '')
    .join('\n');
}